import {gsap} from 'gsap'

// Character set for unscramble effect
const CHAR_LIST = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.!@#$%^&*()_+-=[]{}|/;<>?'

// Get a random character from the character list
const getRandomChar = () => {
  return CHAR_LIST.charAt(Math.floor(Math.random() * CHAR_LIST.length))
}

interface UnscrambleOptions {
  textElement: HTMLElement
  iconElement: HTMLElement
  originalText: string
}

export const animateUnscramble = ({textElement, iconElement, originalText}: UnscrambleOptions) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  // Guard against empty or invalid text
  if (!originalText || originalText.length === 0) return

  const textLength = originalText.length
  // Calculate duration based on text length: shorter text = longer per character
  // For very short text (5 chars): ~0.1s per char, for long text (50 chars): ~0.04s per char
  const baseDuration = Math.max(0.04, 0.5 / textLength)
  const totalDuration = textLength * baseDuration

  // Shift icon left initially
  gsap.set(iconElement, {x: -12})

  // Throttle variables - update every N milliseconds (adjust for desired "framerate")
  const updateInterval = 75 // 75ms = ~13fps, 50ms = ~20fps, 100ms = ~10fps
  let lastUpdateTime = 0

  // Create timeline
  const timeline = gsap.timeline()

  // Animate each character
  timeline.to(
    {},
    {
      duration: totalDuration,
      ease: 'power2.out',
      onUpdate() {
        // Kill timeline if elements are disconnected
        if (!textElement.isConnected || !iconElement.isConnected) {
          timeline.kill()
          return
        }

        const now = Date.now()

        // Skip update if not enough time has passed
        if (now - lastUpdateTime < updateInterval) {
          return
        }

        lastUpdateTime = now
        const progress = this.progress()
        const charsToReveal = Math.floor(progress * textLength)

        // Calculate how many scrambled characters to show ahead
        // For shorter text, show fewer scrambled chars (min 2), for longer text show more (max ~half)
        const scrambledCharsAhead = Math.min(Math.max(2, Math.floor(textLength / 4)), 6)

        // Build the text: revealed chars + limited scrambled chars
        let displayText = ''
        for (let i = 0; i < textLength; i++) {
          if (i < charsToReveal) {
            // Already revealed
            displayText += originalText[i]
          } else if (i < charsToReveal + scrambledCharsAhead) {
            // Show scrambled characters ahead
            displayText += getRandomChar()
          }
        }

        textElement.textContent = displayText
      },
      onComplete: () => {
        // Check again before completing
        if (!textElement.isConnected || !iconElement.isConnected) {
          timeline.kill()
          return
        }

        // Ensure final text is correct
        textElement.textContent = originalText

        // Blink the icon twice
        timeline
          .to(iconElement, {
            x: 0,
            duration: 0.1,
          })
          .to(iconElement, {
            opacity: 0,
            duration: 0.15,
          })
          .to(iconElement, {
            opacity: 1,
            duration: 0.15,
          })
          .to(iconElement, {
            opacity: 0,
            duration: 0.15,
          })
          .to(iconElement, {
            opacity: 1,
            duration: 0.15,
          })
      },
    },
  )
}
